import { Download, ExternalLink, X } from 'react-feather';
import { startOfDay } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import range from 'lodash/range';

import { Breadcrumbs, EventHeader, NavItem, Section, SectionTitle } from '../../Common/Layout';
import { EventHeader as EventHeaderImage } from '../../../common/Common/EventHeader';
import { RegistrationSummary } from '../helpers';
import { getUrlWithoutPrefix, resizedImageUrl } from '../../../common/helpers';
import { useBrandColor } from '../../../common/Common/ThemeProvider';
import { useGuard } from '../Guard';
import { useNavigation } from '../../Common/useNavigation';
import { useParticipant } from '../ParticipantProvider';
import { useRegistrationsUrlState } from './helpers';
import CompanyFundraisingSection from './CompanyFundraisingSection';
import GetRegistrationSummaryForDashboardQuery from './GetRegistrationSummaryForDashboardQuery';
import GetSupportaCampaignsQuery from './GetSupportaCampaignsQuery';
import MessagesSection from './MessagesSection';
import PendingOrdersSection from './PendingOrdersSection';
import PreregistrationsSection from './PreregistrationsSection';
import PrivateFundraisingSection from './PrivateFundraisingSection';
import RegistrationsSection from './RegistrationsSection';
import TeamsSection from './TeamsSection';
import UI from '../../../common/UI';
import UnassignedRegistrationsSection from './UnassignedRegistrationsSection';
import WaitlistRegistrationsSection from './WaitlistRegistrationsSection';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface RegistrationsPageProps {
  participantId: string;
  eventId: string;
  token: string;
}

const RegistrationsPage = ({
  participantId, eventId, token,
}: RegistrationsPageProps) => {
  const { t } = useTranslation();
  const { participant } = useParticipant();

  const [urlState] = useRegistrationsUrlState();

  const { data, refetch } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
        registrations_limit: urlState.registrations.limit || 10,
        registration_filters: {
          search: urlState.registrations.search,
          tickets: urlState.registrations.ticket ? [urlState.registrations.ticket] : null,
          teams: urlState.registrations.team && urlState.registrations.team !== 'no_team'
            ? [urlState.registrations.team] : null,
          has_team: urlState.registrations.team === 'no_team' ? false : null,
          assignment_statuses: urlState.registrations.assignment_statuses,
        },
        preregistrations_limit: urlState.preregistrations.limit || 10,
        waitlist_registrations_limit: urlState.waitlist_registrations.limit || 10,
        teams_ids: urlState.teams.id ? [urlState.teams.id] : undefined,
        teams_search: urlState.teams.search,
        teams_limit: urlState.teams.limit,
        include_filtered_teams: !!urlState.teams.search,
      },
    },
  );

  const summary = data?.registrationSummary;

  useDocumentTitle([
    summary?.participant.full_name,
    summary?.event.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(summary?.event.brand_color);

  const participantRef = useRef(participant);

  // Refetch summary after logging in
  useEffect(() => {
    if (data && participant !== participantRef.current) {
      refetch();
      participantRef.current = participant;
    }
  }, [data, participant, refetch]);

  const hasSingleRequiredCharity = summary
    && summary.event.require_fundraising
    && !summary.event.allow_other_charity
    && summary.event.charities.length === 1;

  const showCompanyFundraisingSection = summary
    && summary.registrations_count > 1
    && (summary.is_company || summary.event_participant.charity)
    && summary.event.enable_fundraising && !hasSingleRequiredCharity;

  const showPrivateFundraisingSection = summary
    && summary.event.enable_fundraising;

  const { data: supportaData } = useQuery(
    GetSupportaCampaignsQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
      },
      skip: !showPrivateFundraisingSection,
    },
  );

  const pendingOrders = summary?.orders.filter((order) => !order.draft && order.remaining_amount > 0) || [];

  if (!data || !themeInitialized) {
    return <UI.PageLoader />;
  }

  const nothingToSee = summary.registrations_count
    + summary.preregistrations_count
    + summary.waitlist_registrations_count
    + summary.teams.length
    + summary.sent_messages.length === 0
    && pendingOrders.length === 0
    && !showCompanyFundraisingSection
    && !showPrivateFundraisingSection;

  return (
    <>
      <Header summary={summary} />

      {urlState.paymentStatus && (
        <UI.Div>
          {urlState.paymentStatus === 'pending' && (
            <UI.Warning>
              {t('common:payments.payment_pending')}
            </UI.Warning>
          )}
          {urlState.paymentStatus !== 'pending' && (
            <UI.Error>
              {t('common:payments.payment_failed')}
            </UI.Error>
          )}
        </UI.Div>
      )}

      {pendingOrders.length > 0 && (
        <PendingOrdersSection summary={summary} pendingOrders={pendingOrders} />
      )}

      {summary.registrations_count > 0 && (
        <>
          {showCompanyFundraisingSection && (
            <CompanyFundraisingSection summary={summary} />
          )}

          {summary.registrations_count > 1 && summary.event.active && (
            <UnassignedRegistrationsSection summary={summary} activeTicketId={urlState.registrations.ticket} />
          )}

          <RegistrationsSection summary={summary} />
        </>
      )}

      {summary.preregistrations_count > 0 && (
        <PreregistrationsSection summary={summary} />
      )}

      {summary.waitlist_registrations_count > 0 && (
        <WaitlistRegistrationsSection summary={summary} />
      )}

      {summary.teams.length > 0 && (
        <TeamsSection summary={summary} />
      )}

      {summary.sent_messages.length > 0 && (
        <MessagesSection messages={summary.sent_messages} />
      )}

      {showPrivateFundraisingSection && (
        <PrivateFundraisingSection
          summary={summary}
          campaigns={supportaData?.registrationSummary.supporta_campaigns}
        />
      )}

      {nothingToSee && (
        <Section>
          <UI.Div sc={{ muted: true }}>
            {t('no_registrations_yet')}
          </UI.Div>
        </Section>
      )}

      <OrganizerSection summary={summary} />
    </>
  );
};

interface HeaderProps {
  summary: RegistrationSummary;
}

const Header = ({ summary }: HeaderProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const { guard } = useGuard(summary.participant);
  const { impersonating } = useParticipant();
  const project = useProject();

  const { event, participant } = summary;

  const ticketsCount = summary.registrations_count;

  const showUpgradesOption = summary.has_upgrades_available;
  const showOrdersOption = summary.orders_count > 0;
  const showExportParticipantsOption = ticketsCount > 1 && summary.is_company;
  const showDownloadTicketsOption = event.enable_tickets && ticketsCount > 0;

  const showOptions = showUpgradesOption
    || showOrdersOption
    || showExportParticipantsOption
    || showDownloadTicketsOption;

  const [openExportModal, closeExportModal] = useModal(() => (
    <ExportParticipantsModal url={summary.export_participants_url} onClose={closeExportModal} />
  ), [summary.export_participants_url]);

  const [openDownloadTicketsModal, closeDownloadTicketsModal] = useModal(() => (
    <DownloadTicketsModal url={summary.tickets_url} ticketsCount={ticketsCount} onClose={closeDownloadTicketsModal} />
  ), [summary.tickets_url, ticketsCount]);

  const ticketsAvailableFrom = event.tickets_available_from
    ? parseDate(summary.event.tickets_available_from, { timezone: 'UTC' })
    : null;

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Strong>{event.title}</UI.Strong>
      </Breadcrumbs>

      {event.header && (
        <EventHeaderImage
          headerUrl={event.header.file.url}
          sportType={event.ticket_categories[0]?.sport_type}
          sc={{ borderRadius: 'xl' }}
          aspectRatio={2.5}
        />
      )}

      <EventHeader event={event} />

      {showOptions && (
        <Section secondary>
          {showUpgradesOption && (
            <NavItem
              to={r('Upgrades', {
                projectId: project.id,
                participantId: participant.id,
                eventId: event.id,
                token: summary.view_token,
              })}
              title={t('order_extras')}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.Span sc={{ fontWeight: 500 }}>
                {t('order_extras')}
              </UI.Span>
            </NavItem>
          )}
          {showOrdersOption && (
            <NavItem
              to={r('Orders', {
                projectId: project.id,
                participantId: participant.id,
                eventId: event.id,
                token: summary.view_token,
              })}
              title={t('orders')}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <UI.Span sc={{ fontWeight: 500 }}>
                  {t('orders')}
                </UI.Span>
                <UI.Badge
                  sc={{
                    background: 'gray.250', outline: false, color: 'gray.500', ml: 2,
                  }}
                  style={{ marginTop: -1, marginBottom: -1 }}
                >
                  {formatNumber(summary.orders_count)}
                </UI.Badge>
              </UI.FlexContainer>
            </NavItem>
          )}
          {showExportParticipantsOption && (
            <NavItem
              onClick={() => guard(openExportModal)}
              title={t('export_participants')}
              icon={<Download />}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.Span sc={{ fontWeight: 500 }} style={{ minWidth: 0 }}>
                <UI.Truncate>
                  {t('export_participants')}
                </UI.Truncate>
              </UI.Span>
            </NavItem>
          )}
          {showDownloadTicketsOption && (
            <NavItem
              href={event.tickets_available && ticketsCount <= 100 ? summary.tickets_url : undefined}
              onClick={event.tickets_available && ticketsCount > 100 ? () => openDownloadTicketsModal() : undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={ticketsCount > 1 ? t('download_tickets') : t('download_ticket')}
              icon={<Download />}
              alignVertical="center"
              sc={{ py: 2 }}
            >
              <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <UI.Span sc={{ fontWeight: 500, muted: !event.tickets_available }} style={{ minWidth: 0 }}>
                  <UI.Truncate>
                    {ticketsCount > 1 ? t('download_tickets') : t('download_ticket')}
                  </UI.Truncate>
                </UI.Span>
                <UI.Span sc={{ flex: {} }}>
                  {!event.tickets_available && ticketsAvailableFrom && (
                    <UI.Span sc={{ ml: 1 }}>
                      <UI.Label
                        sc={{ background: 'gray.250', color: 'gray.600', noWrap: true }}
                        style={{ fontSize: '0.75em' }}
                      >
                        {t('from_date', { date: formatDate(ticketsAvailableFrom, {
                          format: startOfDay(ticketsAvailableFrom).getTime() === ticketsAvailableFrom.getTime()
                            ? 'display_date_without_year'
                            : 'display_date_time_without_year',
                        }) })}
                      </UI.Label>
                    </UI.Span>
                  )}
                  {ticketsCount > 1 && (
                    <UI.Badge
                      sc={{
                        background: 'gray.250', outline: false, color: 'gray.500', ml: 2,
                      }}
                      style={{ marginTop: -1, marginBottom: -1 }}
                    >
                      {formatNumber(ticketsCount)}
                    </UI.Badge>
                  )}
                </UI.Span>

              </UI.FlexContainer>
            </NavItem>
          )}
        </Section>
      )}
      <UI.HR />
    </>
  );
};

const DownloadTicketsModal = ({ url, ticketsCount, onClose }: {
  url: string; ticketsCount: number, onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const batches = range(1, Math.ceil(ticketsCount / 100) + 1);

  return (
    <UI.Modal close={onClose}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('download_tickets')}
          </UI.H4>
          {t('download_tickets_description')}
        </UI.Div>

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {batches.map((batch) => (
            <UI.AButton
              href={`${url}?batch=${batch}`}
              target="_blank"
              rel="noopener noreferrer"
              key={batch}
            >
              <UI.Icon>
                <Download />
              </UI.Icon>
              {' '}
              {t('tickets_x_to_y', {
                x: formatNumber((batch - 1) * 100 + 1),
                y: formatNumber(Math.min(batch * 100, ticketsCount)),
              })}
            </UI.AButton>
          ))}
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Modal>
  );
};

const ExportParticipantsModal = ({ url, onClose }: { url: string; onClose: () => void; }) => {
  const { t } = useTranslation();

  return (
    <UI.Modal close={onClose}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('export_participants')}
          </UI.H4>
          {t('export_participants_description')}
        </UI.Div>

        <UI.GridContainer sc={{ columns: '1fr 1fr', gutter: 0.5 }}>
          <UI.AButton href={`${url}?format=csv`} onClick={onClose}>
            <UI.Icon>
              <Download />
            </UI.Icon>
            {' '}
            {t('csv')}
          </UI.AButton>
          <UI.AButton href={`${url}?format=excel`} onClick={onClose}>
            <UI.Icon>
              <Download />
            </UI.Icon>
            {' '}
            {t('excel')}
          </UI.AButton>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Modal>
  );
};

interface OrganizerSectionProps {
  summary: RegistrationSummary;
}

const OrganizerSection = ({ summary }: OrganizerSectionProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const project = useProject();

  const { event } = summary;

  const email = event.email || project.organisation_email;

  const [showPrivacyStatement, setShowPrivacyStatement] = useState(false);

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.Div>
        <SectionTitle>
          {t('organizer')}
        </SectionTitle>
        <UI.HR sc={{ mt: 1.5, mb: 0 }} />
      </UI.Div>

      <UI.GridContainer sc={{ gutter: 0.5 }}>
        <UI.GridContainer
          sc={{ columns: project.logo ? '42px 1fr' : '1fr', gutter: 0.5, alignVertical: 'center' }}
        >
          {project.logo && (
            <UI.ProfilePic sc={{ background: 'white', size: 42, borderRadius: 'md' }}>
              <img src={resizedImageUrl(project.logo.url, 300, 'webp')} alt={project.organisation_name} />
            </UI.ProfilePic>
          )}
          <UI.Div>
            <UI.H4>
              {project.organisation_name}
            </UI.H4>
            {event.website_url && (
              <UI.Div>
                <UI.A href={event.website_url} target="_blank" rel="noopener noreferrer" sc={{ secondary: true }}>
                  {getUrlWithoutPrefix(event.website_url).split('/')[0]}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.Div>
            )}
            {!event.website_url && email && (
              <UI.Div>
                <UI.A href={`mailto:${email}`} sc={{ secondary: true }}>
                  {email}
                </UI.A>
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>

        {!showPrivacyStatement && (
          <UI.Div>
            <UI.A
              onClick={() => setShowPrivacyStatement((show) => !show)}
              role="button"
            >
              {t('common:privacy_statement.privacy_statement')}
            </UI.A>
          </UI.Div>
        )}

        <UI.AnimateHeight isVisible={showPrivacyStatement}>
          <UI.Card sc={{ outline: 'gray.200', borderRadius: 'lg' }}>
            <UI.A
              onClick={() => setShowPrivacyStatement(false)}
              sc={{ pullRight: true, color: 'gray.800', inlineBlock: true, padding: 0.5 }}
            >
              <UI.Icon>
                <X />
              </UI.Icon>
            </UI.A>
            {project.privacy_statement && (
              <>
                <UI.HTML html={project.privacy_statement} />
                <UI.HR />
              </>
            )}
            <UI.H3>
              {t('common:atleta')}
            </UI.H3>
            <UI.Paragraph>
              {t('common:privacy_statement.atleta_privacy_statement_intro', {
                organizer: project.organisation_name,
              })}
            </UI.Paragraph>
            <UI.A
              href={`https://about.atleta.cc/${locale}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common:privacy_statement.read_atletas_privacy_statement')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </UI.Card>
        </UI.AnimateHeight>
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export default RegistrationsPage;
